import React, { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
} from "@mui/material";
import { hourlyBreakdown } from "./data";
import { formatNumber, formatCurrency, formatDuration } from "./validation";
import { useAuth } from "../../../hooks/AuthProvider";
import { AI_REPORT_REPORT } from "../../../config/urls";
import { formatWithTzLabel } from "../../../utils/dateUtils";

const DetailedReportsTab = ({ startDate, endDate }) => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currency, setCurrency] = useState(null);
  const [sessions, setSessions] = useState({
    sessions: [],
  });

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getValidToken();

      const response = await fetch(
        `${AI_REPORT_REPORT}?from_date=${startDate}&to_date=${endDate}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch report data");

      const json = await response.json();
      if (!json.success) throw new Error("API returned failure");

      const data = json.data;

      setSessions(data.sessions || []);
      setCurrency(data.currency);
    } catch (err) {
      console.error(err);
      setError("Unable to load report data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center">
        {error}
      </Typography>
    );
  }

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Recent Conversations
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Session ID</strong>
                </TableCell>
                <TableCell>
                  <strong>User Phone</strong>
                </TableCell>
                <TableCell>
                  <strong>Start Time</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Messages</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Tokens</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Cost</strong>
                </TableCell>
                <TableCell>
                  <strong>Duration</strong>
                </TableCell>
                <TableCell>
                  <strong>Status</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((row) => (
                <TableRow key={row.session_id} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {row.session_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={row.user_phone} size="small" color="info" />
                  </TableCell>
                  <TableCell>{formatWithTzLabel(row.start_time_iso)}</TableCell>
                  <TableCell align="right">{row.message_count}</TableCell>
                  <TableCell align="right">
                    {formatNumber(row.token_count)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: "error.main", fontWeight: 600 }}
                  >
                    {row.cost_incomplete ? "≥ " : ""}
                    {formatCurrency(row.cost, currency)}
                  </TableCell>
                  <TableCell>{formatDuration(row.duration)}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.status}
                      size="small"
                      color={row.status === "Ended" ? "success" : "warning"}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default DetailedReportsTab;
